import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Subscription : famille "engagement récurrent".
 *
 * Engagement récurrent d'un contact qui ouvre des droits et déclenche une
 * facturation périodique : abonnement, adhésion, contrat, forfait, don
 * mensuel. Construite sur le modèle "famille + type" de la bible (patron :
 * `asset`).
 *
 * Le socle ne connaît pas l'engagement concret ("abonnement salle", "adhésion
 * asso") : il connaît la famille générique `subscription`. Le vocabulaire
 * métier est porté par le champ `type` (chaîne libre déclarée par l'app), le
 * mode de reconduction par `renewalMode`, et l'état du cycle de vie (active,
 * suspendu, résilié, expiré) par `status` : ce sont des champs à part.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `contactId` (l'abonné) est le CŒUR de la famille, donc requis. La formule
 * vendue est pointée soit par `productId` (référence `product`), soit par une
 * référence externe `planRef`. `agreementId` pointe le contrat fondateur
 * éventuel. Les pièces générées en aval (facture, paiement, crédit,
 * réservation) ne vivent PAS ici : c'est l'autre entité qui porte un
 * `subscriptionId` (cf. invoice.subscriptionId).
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible signalé
 * par la fiche.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille subscription (hors champs standard du socle). */
export const subscriptionCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "abonnement", "adhesion", "contrat", "forfait", "don"). */
  type: z.string().min(1),
  /** Contact engagé (référence `contact`, cœur de la famille). Requis. */
  contactId: z.string().min(1),
  /** Formule vendue (référence `product`), optionnel. */
  productId: z.string().min(1).optional(),
  /** Référence de formule externe si pas de `product` interne, optionnel. */
  planRef: z.string().min(1).optional(),
  /** Périodicité de facturation, chaîne libre pilotée par l'app (ex. "mensuel", "annuel"). Optionnel. */
  billingPeriod: z.string().min(1).optional(),
  /** Mode de reconduction, chaîne libre pilotée par l'app (ex. "auto", "manuel"). Optionnel. */
  renewalMode: z.string().min(1).optional(),
  /** État du cycle de vie, chaîne libre pilotée par l'app (ex. "active", "suspendu", "resilie", "expire"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Début de l'engagement. Optionnel. */
  startAt: z.date().optional(),
  /** Fin de l'engagement (vide tant qu'il court). Optionnel. */
  endAt: z.date().optional(),
  /** Date de la prochaine échéance de facturation. Optionnel. */
  nextInvoiceAt: z.date().optional(),
  /** Contrat / accord fondateur (référence `agreement`), optionnel. */
  agreementId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille subscription (champs standard + champs métier). */
export const SubscriptionSchema = z.object({
  ...baseEntityFields,
  ...subscriptionCoreFields,
});

/** Type d'une subscription. */
export type Subscription = z.infer<typeof SubscriptionSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const SubscriptionCreateInputSchema = z.object({
  ...subscriptionCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une subscription. */
export type SubscriptionCreateInput = z.infer<typeof SubscriptionCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const SubscriptionUpdateInputSchema = z
  .object({ ...subscriptionCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une subscription. */
export type SubscriptionUpdateInput = z.infer<typeof SubscriptionUpdateInputSchema>;
